import type { ChatMessage, ReadModalityYesNoPrompt } from '../types/chat'
import { isFrenchLanguage } from './chatUiLocale'

export function parseReadModalityConfirmFromMetadata(
  md: Record<string, unknown>,
): ReadModalityYesNoPrompt | undefined {
  const ctx = String(md.general_confirmation_context ?? md.generalConfirmationContext ?? '')
    .trim()
    .toLowerCase()
  const awaiting =
    md.awaiting_general_confirmation === true || md.awaitingGeneralConfirmation === true
  const docRaw = md.read_document_confirm ?? md.readDocumentConfirm
  if (docRaw && typeof docRaw === 'object') {
    const label = String(
      (docRaw as { document_label?: string; documentLabel?: string }).document_label
        ?? (docRaw as { documentLabel?: string }).documentLabel
        ?? '',
    ).trim()
    const language = String((docRaw as { language?: string }).language || '').trim() || undefined
    return { kind: 'read_aloud', modalityLabel: label || undefined, language }
  }
  const raw = md.read_modality_confirm ?? md.readModalityConfirm
  if (raw && typeof raw === 'object') {
    const label = String((raw as { modality_label?: string; modalityLabel?: string }).modality_label
      ?? (raw as { modalityLabel?: string }).modalityLabel
      ?? '').trim()
    const language = String((raw as { language?: string }).language || '').trim() || undefined
    return { kind: 'read_aloud', modalityLabel: label || undefined, language }
  }
  if ((ctx === 'read_modality' || ctx === 'read_document') && awaiting) {
    return { kind: 'read_aloud' }
  }
  return undefined
}

export function isReadModalityConfirmationMessage(m: ChatMessage): boolean {
  if (m.readModalityYesNoPrompt) return true
  const ctx = (m.generalConfirmationContext || '').trim().toLowerCase()
  return (ctx === 'read_modality' || ctx === 'read_document') && m.awaitingGeneralConfirmation === true
}

export function readModalityConfirmIsActionable(m: ChatMessage, hist: boolean): boolean {
  if (hist || m.readModalityYesNoResolved) return false
  return isReadModalityConfirmationMessage(m)
}

export function readModalityUiFrench(m: ChatMessage): boolean {
  const lang = (m.readModalityYesNoPrompt?.language || '').trim().toLowerCase()
  if (lang === 'french' || lang === 'fr') return true
  return isFrenchLanguage()
}

export function markReadModalityConfirmResolved(
  messages: ChatMessage[],
  messageId?: string,
): ChatMessage[] {
  return messages.map((m) => {
    if (!isReadModalityConfirmationMessage(m) || m.readModalityYesNoResolved) return m
    if (messageId && m.id !== messageId) return m
    return {
      ...m,
      readModalityYesNoResolved: true,
      awaitingGeneralConfirmation: false,
    }
  })
}

/** Last assistant row still waiting for read-aloud Yes/No. */
export function findPendingReadModalityConfirm(
  messages: readonly ChatMessage[],
): ChatMessage | null {
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i]
    if (m.historySnapshot) continue
    if (m.role !== 'assistant') continue
    if (readModalityConfirmIsActionable(m, false)) return m
  }
  return null
}

export function parseReadModalityConfirmAnswer(text: string): 'yes' | 'no' | null {
  const t = (text || '').trim().toLowerCase().replace(/[^\w\s']/g, ' ')
  const norm = t.replace(/\s+/g, ' ').trim()
  if (!norm) return null
  if (
    /^(no|non|nope|cancel|stop|annule|annuler)\b/.test(norm) ||
    /\b(no\s+cancel|non\s+annuler)\b/.test(norm)
  ) {
    return 'no'
  }
  if (
    /^(yes|yeah|yep|oui|ok|okay)\b/.test(norm) ||
    /\b(yes\s+proceed|oui\s+continuer|read\s+it|go\s+ahead)\b/.test(norm)
  ) {
    return 'yes'
  }
  return null
}

/** Read-modality confirm must hit ``/chat/command`` (Textract + Polly on the backend). */
export function shouldUseHostedChatForReadModalityConfirm(
  text: string,
  messages: readonly ChatMessage[],
): boolean {
  if (!findPendingReadModalityConfirm(messages)) return false
  return parseReadModalityConfirmAnswer(text) !== null
}

/** Do not treat read-aloud Yes/No as a stale patient-switch follow-up. */
export function readModalityShouldBypassPatientSwitch(
  messages: readonly ChatMessage[],
  text: string,
): boolean {
  return shouldUseHostedChatForReadModalityConfirm(text, messages)
}
